// ============================================================================
// CHANGO V5.0 — D3 Products List (Task #64)
// ============================================================================
// Mount: /app/products
//
// Workspace-scoped product catalog listing. Sales users see sku/name/price;
// boss/finance also see the cost column (§20 #17 — the backend omits
// default_cost_rmb from the payload for sales, so this component is a second
// defense at the UI layer).
//
// Supported query params:
//   ?q=        substring over name / sku / description
//   ?archived= 0 (default) | 1 | all
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import ExcelImportModal from '../../components/ExcelImportModal.jsx';

function money(v, currency) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const sym = currency === 'USD' ? '$'
            : currency === 'EUR' ? '€'
            : currency === 'JPY' ? '¥'
            : currency === 'GBP' ? '£'
            : '¥';
  return `${sym}${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ProductsList() {
  const { user } = useAuth() || {};
  const isSales = user?.role === 'sales';

  const [params, setParams] = useSearchParams();
  const qFilter = params.get('q') || '';
  const archivedFilter = params.get('archived') || '0';

  const [rows, setRows]   = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (qFilter) qs.set('q', qFilter);
    if (archivedFilter && archivedFilter !== '0') qs.set('archived', archivedFilter);
    qs.set('limit', '50');
    (async () => {
      try {
        const res = await api.get(`/api/products?${qs.toString()}`);
        if (cancelled) return;
        setRows(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number.isInteger(res?.total) ? res.total : 0);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setRows([]); setTotal(0);
        } else {
          setError(err?.message || '加载产品列表失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [qFilter, archivedFilter]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else       next.delete(key);
    setParams(next, { replace: true });
  };

  const colCount = isSales ? 5 : 6;

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">产品</h1>
          <p className="text-sm text-slate-500">共 {total} 条</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="搜索 SKU / 名称…"
            defaultValue={qFilter}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setFilter('q', e.currentTarget.value.trim());
            }}
            className="input-text w-60"
          />
          <select
            value={archivedFilter}
            onChange={(e) => setFilter('archived', e.target.value === '0' ? '' : e.target.value)}
            className="input-text"
          >
            <option value="0">活跃</option>
            <option value="1">已归档</option>
            <option value="all">全部</option>
          </select>
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary"
          >
            📥 批量导入
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              const qs = new URLSearchParams();
              if (qFilter) qs.set('q', qFilter);
              if (archivedFilter && archivedFilter !== '0') qs.set('archived', archivedFilter);
              window.open(`/api/products/export-excel?${qs.toString()}`, '_blank');
            }}
          >
            导出 Excel
          </button>
          <Link to="/app/products/new" className="btn-primary">
            新建产品
          </Link>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">名称</th>
              <th className="px-4 py-3">单位</th>
              <th className="px-4 py-3 text-right">默认售价</th>
              {!isSales && <th className="px-4 py-3 text-right">默认成本</th>}
              <th className="px-4 py-3">状态</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={colCount} className="px-4 py-8 text-center text-slate-400">加载中…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-4 py-8 text-center text-slate-400">
                  暂无产品 · <Link to="/app/products/new" className="text-chango-700 hover:underline">创建第一条</Link>
                </td>
              </tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs text-slate-700">
                  <Link to={`/app/products/${r.id}`} className="text-chango-700 hover:underline">
                    {r.sku_code}
                  </Link>
                </td>
                <td className="px-4 py-3">{r.name || '—'}</td>
                <td className="px-4 py-3 text-slate-500">{r.unit || '—'}</td>
                <td className="px-4 py-3 text-right text-slate-700">
                  {money(r.default_price_rmb, r.currency)}
                </td>
                {!isSales && (
                  <td className="px-4 py-3 text-right text-slate-500">
                    {money(r.default_cost_rmb, r.currency)}
                  </td>
                )}
                <td className="px-4 py-3">
                  {r.archived_at
                    ? <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">已归档</span>
                    : <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">活跃</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showImportModal && (
        <ExcelImportModal
          entity="products"
          onClose={() => setShowImportModal(false)}
          onComplete={() => {
            setShowImportModal(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
